import { useTranslation } from 'react-i18next';

import { FaqList, type FaqEntry } from './faq-list';
import { NavLink } from './nav-link';
import { Section } from './section';
import type { Collection } from '../lib/collections';
import { localePath } from '../lib/format';
import { useLocale } from '@todo/core/i18n/use-locale';

import css from './collection-guide.module.css';

/** One subsection of a collection's guide: an <h3> over paragraphs. */
export type GuidePart = { h: string; p: string[] };

export type CollectionCopy = { guide: GuidePart[]; faq: FaqEntry[] };

/**
 * The guide and FAQ of a collection, from `collections.<slug>.guide` /
 * `.faq` in the i18n resources — the categories, topics and areas but
 * downtown have them, the moods and downtown don't (yet), so both lists are
 * empty for those.
 */
export function useCollectionCopy(slug: string): CollectionCopy {
  const { t } = useTranslation();
  const list = <T,>(key: string): T[] => {
    const value: unknown = t(`collections.${slug}.${key}`, { returnObjects: true });
    return Array.isArray(value) ? (value as T[]) : [];
  };
  return { guide: list<GuidePart>('guide'), faq: list<FaqEntry>('faq') };
}

/** A page that isn't a collection, for TopicLinks: its path under the locale and the key of its label. */
export type SeeAlsoPage = { path: string; labelKey: string };

// The line under a collection's intro that links a category to its topics
// (/scenic-flights → /hot-air-balloon) and a topic back to its category —
// plus, after them, any other pages the site sends readers on to (`pages`).
export function TopicLinks({ collections, pages = [] }: { collections: Collection[]; pages?: readonly SeeAlsoPage[] }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  return (
    <p className={css.topics}>
      <span className={css.topicsLabel}>{t('collections.seeAlso')}</span>
      {collections.map((c) => (
        <NavLink key={c.slug} href={localePath(locale, c.slug)} className={css.topic}>
          {t(`collections.${c.slug}.title`)}
        </NavLink>
      ))}
      {pages.map((p) => (
        <NavLink key={p.path} href={localePath(locale, p.path)} className={css.topic}>
          {t(p.labelKey)}
        </NavLink>
      ))}
    </p>
  );
}

// The copy under a collection's cards: what to know before booking (the page's
// long-form text for search) and an FAQ, mirrored as FAQPage JSON-LD by the
// page from the same entries.
export function CollectionGuide({ slug, copy }: { slug: string; copy: CollectionCopy }) {
  const { t } = useTranslation();
  return (
    <>
      {copy.guide.length ? (
        <Section id="collection-guide" title={t(`collections.${slug}.guideTitle`)}>
          <div className={css.parts}>
            {copy.guide.map((part) => (
              <article key={part.h} className={css.part}>
                <h3 className={css.heading}>{part.h}</h3>
                {part.p.map((text) => (
                  <p key={text} className={css.text}>
                    {text}
                  </p>
                ))}
              </article>
            ))}
          </div>
        </Section>
      ) : null}
      {copy.faq.length ? (
        <Section id="collection-faq" title={t('collections.faqTitle')}>
          <FaqList entries={copy.faq} />
        </Section>
      ) : null}
    </>
  );
}
